<script setup lang="ts">
import TextWithTooltips from '@jaaluu/components/TextWithTooltips.vue';
import stores from '@jaaluu/stores';
import { t } from '@shared/i18n';
import { ChatService, OpenAiRoleType } from '@shared/openapi/jaaluu-api';
import { Ref, nextTick, ref } from 'vue';

const props = defineProps({
  word: {
    type: String,
    required: true
  },
  contextSentence: {
    type: String,
    required: false,
    default: null
  }
});

const { langForeign, langNative } = stores.language.state;
const { handleDefaultError } = stores.error.funcs;
const { logoBallTrans } = stores.assets.state;

const chat: Ref<{ content: string; role: string }[]> = ref([]);
const userMessage = ref('');
const isSending = ref(false);
const chatId: Ref<string | null> = ref(null);
const initialized = ref(false);


const scrollToBottom = () => {
  setTimeout(() => {
    let el: HTMLElement | null = document.getElementById('scrollAnchorChat');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
  }, 500);
};

const initChat = async () => {
  if (initialized.value || !langForeign.value || !langNative.value) return;
  initialized.value = true;

  const context = props.contextSentence
    ? ` The user clicked on the word "${props.word}" in the sentence: "${props.contextSentence}".`
    : ` The user wants to learn about the word "${props.word}".`;

  const systemMsg = `You are a friendly ${langForeign.value} language teacher. Help the user learn and practice ${langForeign.value}.${context} Start by greeting the user and asking if they have questions about this word or want to practice using it.`;

  const res: any = await ChatService.addChatMessageV1ChatAddChatMessagePost({
    query: { lang: langForeign.value, chat_id: null },
    body: [{ role: OpenAiRoleType.SYSTEM, content: systemMsg }]
  }).catch(handleDefaultError);

  if (!res?.data?.chat_id) return;
  chatId.value = res.data.chat_id;

  const initRes: any = await ChatService.answerToTextV1ChatAnswerToTextPost({
    query: { chat_id: chatId.value },
    body: { lang: langForeign.value, text: '__init__' }
  }).catch(handleDefaultError);

  if (initRes?.data?.chat) {
    chat.value = initRes.data.chat.filter(
      (m: any) => m.role !== OpenAiRoleType.SYSTEM && m.content !== '__init__'
    );
    chatId.value = initRes.data.chat_id;
    scrollToBottom();
  }
};

const sendMessage = async () => {
  if (!langForeign.value || isSending.value || !userMessage.value.trim()) return;
  isSending.value = true;
  const text = userMessage.value;
  userMessage.value = '';

  const res: any = await ChatService.answerToTextV1ChatAnswerToTextPost({
    query: { chat_id: chatId.value },
    body: { lang: langForeign.value, text }
  }).catch(handleDefaultError);

  if (res?.data?.chat) {
    chat.value = res.data.chat.filter((m: any) => m.role !== OpenAiRoleType.SYSTEM && m.content !== '__init__');
    chatId.value = res.data.chat_id;
    scrollToBottom();
  }
  isSending.value = false;
};
</script>

<template>
  <div @vue:mounted="initChat">
    <div v-for="(message, idx) in chat" :key="idx" class="d-flex mt-2"
      :class="{ 'justify-end': message.role === OpenAiRoleType.ASSISTANT }">
      <v-card
        class="pa-2 msg"
        elevation="2"
        :class="{
          'msg-assistant': message.role === OpenAiRoleType.ASSISTANT,
          'msg-user': message.role === OpenAiRoleType.USER
        }"
        :color="message.role === OpenAiRoleType.ASSISTANT ? undefined : 'primary'"
      >
        <v-avatar size="x-small" v-if="message.role === OpenAiRoleType.ASSISTANT">
          <v-img alt="Jaaluu" :src="logoBallTrans" />
        </v-avatar>
        <v-avatar size="x-small" color="primary-darken-1" v-else>
          <v-icon size="x-small" icon="mdi-account-circle" />
        </v-avatar>
        <div class="ml-2">
          <TextWithTooltips :text="message.content" />
        </div>
      </v-card>
    </div>

    <div id="scrollAnchorChat" />
    <v-skeleton-loader v-if="!initialized || (isSending)" type="sentences" height="40" class="mt-2" />

    <div class="d-flex align-end mt-3">
      <v-textarea
        v-model="userMessage"
        rows="1"
        variant="underlined"
        clearable
        color="green"
        density="compact"
        hide-details
        :auto-grow="true"
        clear-icon="mdi-close"
        :placeholder="t('chat.your_message')"
        class="search mb-10 mt-4"
        @keydown.enter.prevent="sendMessage"
      />
      <v-btn
        v-if="userMessage"
        size="x-small"
        icon="mdi-send"
        :loading="isSending"
        color="green"
        variant="text"
        @click="sendMessage"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.msg {
  max-width: 85%;
  display: flex;
  align-items: flex-start;
}
.msg-assistant {
  justify-content: flex-end;
}
.msg-user {
  float: left;
}
.search {
  width: 100%;
}
</style>
